import { useState } from "react";
import { Box, Flex, Heading, Text, VStack, Input, HStack } from "@chakra-ui/react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { colors } from "@/theme/colors";
import { checkPassword, scorePassword } from "@/lib/passwordPolicy";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <Flex minH="100vh" align="center" justify="center" bg={colors.page} p="4">
    <Box w="full" maxW="440px">
      <VStack gap="3" mb="7">
        <Logo size={46} />
        <Heading fontFamily="heading" fontWeight="700" fontSize="2xl" letterSpacing="-0.01em">
          <Box as="span" color={colors.teal}>Hakina</Box>{" "}
          <Box as="span" color={colors.textPrimary}>Safety</Box>
        </Heading>
      </VStack>
      <Box
        bg={colors.white}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="16px"
        p={{ base: "6", md: "8" }}
        boxShadow="0 1px 2px rgba(31,41,55,0.04)"
      >
        <Heading fontFamily="heading" fontWeight="700" fontSize="xl" mb="1" color={colors.textPrimary}>
          {title}
        </Heading>
        {subtitle && (
          <Text color={colors.textSecondary} fontSize="sm" mb="6">
            {subtitle}
          </Text>
        )}
        {children}
      </Box>
      {footer && (
        <Box mt="5" textAlign="center" fontSize="sm" color={colors.textSecondary}>
          {footer}
        </Box>
      )}
    </Box>
  </Flex>
);

export const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>
      {label}
    </Text>
    {children}
  </Box>
);

export const inputStyle = {
  bg: colors.page,
  borderColor: "#D9D3C7",
  borderWidth: "1px",
  height: "44px",
  _hover: { borderColor: "#C7C0B2" },
  _focusVisible: {
    borderColor: colors.teal,
    boxShadow: `0 0 0 1px ${colors.teal}`,
    bg: colors.white,
  },
};

export const ErrorNote = ({ children, ...rest }) =>
  children ? (
    <Box
      bg={colors.poppyTint}
      color={colors.poppy}
      borderRadius="10px"
      px="4"
      py="2.5"
      fontSize="sm"
      data-testid="auth-error"
      {...rest}
    >
      {children}
    </Box>
  ) : null;

export const SuccessNote = ({ children, ...rest }) =>
  children ? (
    <Box
      bg={colors.resolvedBg}
      color={colors.resolvedText}
      borderRadius="10px"
      px="4"
      py="2.5"
      fontSize="sm"
      data-testid="auth-success"
      {...rest}
    >
      {children}
    </Box>
  ) : null;

export const PasswordStrength = ({ value }) => {
  const { level, label, color, pct } = scorePassword(value || "");
  if (!value) return null;
  return (
    <Box mt="2" data-testid="password-strength">
      <Box position="relative" h="6px" borderRadius="full" bg="#EDE8DE" overflow="hidden">
        <Box position="absolute" left="0" top="0" bottom="0" borderRadius="full"
          w={`${pct}%`} bg={color} transition="width 0.25s ease, background-color 0.25s ease" />
      </Box>
      {label && (
        <Text fontSize="xs" mt="1" fontWeight="600" color={color} data-testid={`password-strength-label-${label.toLowerCase()}`}>
          {label}
        </Text>
      )}
    </Box>
  );
};

export const PasswordRequirements = ({ value }) => {
  const { checks } = checkPassword(value || "");
  return (
    <VStack align="stretch" gap="1.5" mt="2.5" data-testid="password-requirements">
      {checks.map((c) => (
        <HStack key={c.key} gap="2" fontSize="xs">
          <Flex align="center" justify="center" w="16px" h="16px" borderRadius="full"
            bg={c.met ? colors.resolvedBg : colors.cream}
            color={c.met ? colors.resolvedText : colors.textSecondary}
            data-testid={`pw-check-${c.key}${c.met ? "-met" : ""}`}>
            {c.met ? <Check size={11} strokeWidth={3} /> : <X size={11} strokeWidth={3} />}
          </Flex>
          <Text color={c.met ? colors.textPrimary : colors.textSecondary}>{c.label}</Text>
        </HStack>
      ))}
    </VStack>
  );
};

export const PasswordInput = ({ value, onChange, "data-testid": testId, ...rest }) => {
  const [show, setShow] = useState(false);
  return (
    <Box position="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        data-testid={testId}
        pr="46px"
        {...inputStyle}
        {...rest}
      />
      <Flex
        as="button"
        type="button"
        position="absolute"
        right="6px"
        top="0"
        bottom="0"
        my="auto"
        align="center"
        justify="center"
        w="34px"
        h="34px"
        borderRadius="8px"
        color={colors.textSecondary}
        _hover={{ color: colors.tealHover, bg: colors.cream }}
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={testId ? `${testId}-toggle` : "password-toggle"}
        tabIndex={-1}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </Flex>
    </Box>
  );
};
